import type { DealProviderConfig } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import type { ListDealBase, OAListDeal } from '@openx/types/deal.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useQuery } from '@tanstack/react-query';
import { GetDealsListDocument } from '../../../graphql/query/deals/GetDealsList.generated.tsx';
import type { DealsQueryCriteria } from '../../../modules/deals/DealsList/types.ts';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types.ts';
import { buildDealsQueryString } from './buildDealsQueryString.ts';

export const useDealsListFetch: DealProviderConfig['useDealsListFetch'] = <T extends ListDealBase = OAListDeal>(
  queryCrit: DealsQueryCriteria | undefined
) => {
  const queryString = buildDealsQueryString(queryCrit);

  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryFn: ({ client }) => getGraphQLRequestFromClient(client)(GetDealsListDocument, { queryString }),
    queryKey: [QueryKeys.DEALS, 'list', queryString],
    staleTime: Infinity,
  });

  const deals = data?.deals?.objects?.map(dealRaw => ({
    ...dealRaw,
    deal_id: dealRaw.exchange_deal_id,
    deal_participants: dealRaw.exchange_deal?.deal_participants,
    synchronized_with: dealRaw.exchange_deal?.synchronized_with,
    uid: dealRaw.exchange_uid,
  }));

  return {
    dealsList: deals as unknown as T[],
    error: error ?? undefined,
    hasMore: data?.deals?.has_more,
    loading,
  };
};
